import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PauseIcon from "@mui/icons-material/Pause";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SettingsIcon from "@mui/icons-material/Settings";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";
import { FaShoppingBasket } from "react-icons/fa";
import { useConfetti } from "../../../context/ConfettiContext";
import PlayElapsedTimer from "../../../pages/puzzles/common/components/PlayElapsedTimer";
import CanvasBoard from "../../../pages/puzzles/jigsaw/CanvasBoard";
import JigsawColorBasketOverlay from "../../../pages/puzzles/jigsaw/JigsawColorBasketOverlay";
import { PIECE_SHINE_CLEANUP_MS } from "../../../pages/puzzles/jigsaw/PieceLockShineOverlay";
import PieceTrayScrollRows from "../../../pages/puzzles/jigsaw/PieceTrayScrollRows";
import {
    BOARD_SIZE,
    PieceVisual,
    PIECE_VISUAL_MARGIN_RATIO,
    pieceVisualCellOffsetPx,
    pieceVisualRenderSizePx,
} from "../../../pages/puzzles/jigsaw/PieceVisual";
import { useBoardMatColor } from "../../../pages/puzzles/jigsaw/useBoardMatColor";
import SlidingPlayMistBackground from "../../../pages/puzzles/sliding/SlidingPlayMistBackground";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import {
    animatePieceAlongCurve,
    flyControlPoint,
} from "../../mobile-jigsaw-animation/lib/pieceFlyAnimation";
import ColorBasketFeatureShot from "../components/ColorBasketFeatureShot";
import PieceTrayScrollColumns from "../components/PieceTrayScrollColumns";
import { getImageUrl, getStoryTitle, getTemplateGrid } from "../lib/api";
import { loadColorBasketDemoPuzzle } from "../lib/colorBasketDemo";
import { RECORDING_FRAME_GOOGLE_1080P } from "../recordingCaptureFrame";
import {
    computeBoardSidePx,
    PAD,
    TOOLBAR_COL_W,
    TOOLBAR_GAP,
    TOOLBAR_ICON_SX,
    TRAY_CELL_GAP,
    TRAY_GAP,
    TRAY_PIECE_SCALE,
    trayBlockPx,
} from "../recordingCaptureLayout";
import { RECORDING_CONTENT_STORY_ID } from "../recordingContentPuzzle";
import { useCaptureFrameViewport } from "../useCaptureFrameViewport";

const FRAME = RECORDING_FRAME_GOOGLE_1080P;

/**
 * Take length is about 3:23 — slow enough to follow every placement on video.
 */
/** Opening feature shot: one full pass of its three highlighted steps (3s each). */
const FEATURE_SHOT_MS = 9_000;
/** Pieces dropped on the main board before the color baskets are opened. */
const SEED_PIECE_COUNT = 5;
const SEED_FLY_MS = 1800;
const SEED_GAP_MS = 900;
/**
 * The basket run shares a time budget instead of a fixed per-piece speed, so any basket size
 * (5 pieces or 40) keeps the take roughly the same length.
 */
const BASKET_RUN_BUDGET_MS = 95_000;
const BASKET_FLY_MIN_MS = 900;
const BASKET_FLY_MAX_MS = 3000;
const BASKET_GAP_MS = 600;
/**
 * Finish: a few more single pieces for motion, then the rest fills in batches. One React
 * render per piece cannot keep up with a 144-piece board, and the sweep reads better anyway.
 */
const FINISH_SOLO_COUNT = 6;
const FINISH_SOLO_FLY_MS = 1200;
const FINISH_SOLO_GAP_MS = 500;
const FINISH_SWEEP_BATCH = 4;
const FINISH_SWEEP_STEP_MS = 1400;

const INTRO_HOLD_MS = 2500;
/** Basket toolbar button pulses before the chooser opens, as if it was tapped. */
const BASKET_TAP_HOLD_MS = 2000;
const CHOOSER_PICK_MS = 3500;
const BASKET_DONE_HOLD_MS = 3000;
const MIRROR_HOLD_MS = 5000;
const SOLVED_HOLD_MS = 16_000;
const LOOP_GAP_MS = 4000;

const PHASE = {
    LOADING: "loading",
    FEATURE: "feature",
    SEED: "seed",
    BASKET_TAP: "basket-tap",
    CHOOSER: "chooser",
    BASKET: "basket",
    MIRROR: "mirror",
    FINISH: "finish",
    SOLVED: "solved",
};

const basketTapPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(59, 166, 102, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(59, 166, 102, 0.16), 0 0 18px rgba(59, 166, 102, 0.75);
  }
`;

function newPieceId(pieceIndex) {
    return `demo-${pieceIndex}-${Math.random().toString(36).slice(2, 8)}`;
}

function shuffleIndices(count, seedKey = "") {
    const arr = Array.from({ length: count }, (_, i) => i);
    let h = 0;
    const key = String(seedKey);
    for (let c = 0; c < key.length; c += 1) h = (h * 31 + key.charCodeAt(c)) | 0;
    let s = (h ^ 0x9e3779b9) >>> 0;
    const rand = () => {
        s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
        return s / 0x100000000;
    };
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    }
    return arr;
}

/** Screen rect of a piece resting on its home cell of a rendered board layer. */
function homeScreenRect(pieceIndex, gridN, boardLayerEl) {
    if (!boardLayerEl || !(gridN > 0)) return null;
    const rect = boardLayerEl.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const displayScale = rect.width / BOARD_SIZE;
    const cellPx = BOARD_SIZE / gridN;
    const offset = pieceVisualCellOffsetPx(cellPx);
    const size = pieceVisualRenderSizePx(cellPx) * displayScale;
    return {
        left: rect.left + ((pieceIndex % gridN) * cellPx - offset) * displayScale,
        top: rect.top + (Math.floor(pieceIndex / gridN) * cellPx - offset) * displayScale,
        width: size,
        height: size,
    };
}

/** Per-piece flight time so a whole run fits the budget. */
function paceFlyMs(count, budgetMs, minMs, maxMs, gapMs) {
    if (count <= 0) return maxMs;
    const perPiece = budgetMs / count - gapMs;
    return Math.round(Math.min(maxMs, Math.max(minMs, perPiece)));
}

function homePiece(pieceIndex, gridN, cellPxN) {
    return {
        id: newPieceId(pieceIndex),
        pieceIndex,
        x: (pieceIndex % gridN) * cellPxN,
        y: Math.floor(pieceIndex / gridN) * cellPxN,
    };
}

/**
 * Prototype-only 1920×1080 Color Basket trailer: seeds a few pieces on the main board, opens
 * the real basket chooser, fills one basket board, mirrors it back onto the main board, then
 * completes the puzzle — hands-free, on a loop. Local state only: nothing is saved.
 */
export default function Recording1080pColorBasketPage() {
    const { triggerConfetti } = useConfetti();
    const {
        mat: boardMat,
        grooveStyle: boardGrooveStyle,
    } = useBoardMatColor();

    const mainBoardRef = useRef(null);
    const basketBoardRef = useRef(null);
    const mainTrayRef = useRef(null);
    const basketTrayRef = useRef(null);
    const flyNodeRef = useRef(null);
    const flyInnerRef = useRef(null);
    /** `${lane}:${pieceIndex}` → tray slot element (flight start rect) */
    const traySlotRefs = useRef(new Map());
    /** Pending waits and flights, resolved on cancel so the runner can exit. */
    const pendingRef = useRef([]);
    const fxTimersRef = useRef([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [story, setStory] = useState(null);
    const [template, setTemplate] = useState(null);
    const [baskets, setBaskets] = useState([]);
    const [loopIndex, setLoopIndex] = useState(0);
    const [phase, setPhase] = useState(PHASE.LOADING);
    const [mainPieces, setMainPieces] = useState([]);
    const [basketPieces, setBasketPieces] = useState([]);
    const [shines, setShines] = useState([]);
    /** In-flight piece: { lane, pieceIndex, frame } */
    const [flying, setFlying] = useState(null);

    const mainPiecesRef = useRef([]);
    mainPiecesRef.current = mainPieces;
    const basketPiecesRef = useRef([]);
    basketPiecesRef.current = basketPieces;
    /**
     * Values the runner reads but must not restart for: `triggerConfetti` is a new function on
     * every provider render, and the queues are derived from the loop's shuffle.
     */
    const runtimeRef = useRef({});

    useCaptureFrameViewport(FRAME.width, FRAME.height);

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const loaded = await loadColorBasketDemoPuzzle();
                if (cancelled) return;
                setStory(loaded.story);
                setTemplate(loaded.template);
                setBaskets(loaded.baskets);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load the color basket demo");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const grid = getTemplateGrid(template) || 0;
    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const total = grid * grid;
    const imageUrl = getImageUrl(story);
    const paths = template?.piecePaths?.pieces;
    const svgPathFallback = template?.svgPath;
    const puzzleTitle = getStoryTitle(story);

    const basket = baskets[0] ?? null;
    const basketIndex = Number(basket?.index ?? 0);
    const basketHex = basket?.dominantColorHex || "#3BA666";
    const basketPieceIds = useMemo(() => {
        const raw = Array.isArray(basket?.pieceIds) ? basket.pieceIds : [];
        return raw
            .map(Number)
            .filter((id) => Number.isInteger(id) && id >= 0 && id < total)
            .sort((a, b) => a - b);
    }, [basket, total]);

    const trayOrder = useMemo(
        () => (total > 0 ? shuffleIndices(total, `${template?.id || "demo"}-${loopIndex}`) : []),
        [total, template?.id, loopIndex],
    );

    const boardSide = useMemo(
        () => computeBoardSidePx(grid, FRAME.width, FRAME.height),
        [grid],
    );
    const trayScale = (boardSide > 0 ? boardSide / BOARD_SIZE : 1) * TRAY_PIECE_SCALE;
    const trayPiecePx = Math.round(pieceVisualRenderSizePx(cellPx) * trayScale);
    const trayCirclePx = trayPiecePx + 2;
    const trayThicknessPx = trayBlockPx(trayCirclePx, true);
    const pieceNativePx = pieceVisualRenderSizePx(cellPx);

    const mainOnBoard = useMemo(
        () => new Set(mainPieces.map((b) => b.pieceIndex)),
        [mainPieces],
    );
    const mainTrayPieces = useMemo(
        () => trayOrder.filter((idx) => !mainOnBoard.has(idx)),
        [trayOrder, mainOnBoard],
    );
    const basketOnBoard = useMemo(
        () => new Set(basketPieces.map((b) => b.pieceIndex)),
        [basketPieces],
    );
    const basketTrayPieces = useMemo(
        () => basketPieceIds.filter((idx) => !basketOnBoard.has(idx) && !mainOnBoard.has(idx)),
        [basketPieceIds, basketOnBoard, mainOnBoard],
    );

    const isSolved = total > 0 && mainPieces.length >= total;
    const onBasketScene = phase === PHASE.BASKET;

    const regionGuide = useMemo(
        () => ({ pieceIndexes: basketPieceIds, colorHex: basketHex }),
        [basketPieceIds, basketHex],
    );

    const triggerShine = useCallback((boardId) => {
        const id = `${boardId}-${Math.random().toString(36).slice(2, 7)}`;
        setShines((prev) => [...prev, { boardId, id }]);
        fxTimersRef.current.push(
            window.setTimeout(() => {
                setShines((prev) => prev.filter((entry) => entry.id !== id));
            }, PIECE_SHINE_CLEANUP_MS),
        );
    }, []);

    /** Resolve every pending wait/flight so a cancelled run can unwind. */
    const abortPending = useCallback(() => {
        const entries = pendingRef.current;
        pendingRef.current = [];
        for (const entry of entries) {
            if (entry.timerId) window.clearTimeout(entry.timerId);
            entry.cancel?.();
            entry.resolve();
        }
        for (const timer of fxTimersRef.current) window.clearTimeout(timer);
        fxTimersRef.current = [];
    }, []);

    runtimeRef.current = {
        basketPieceIds,
        trayOrder,
        triggerConfetti,
        triggerShine,
    };

    /** Move the in-flight piece by mutating its node (no re-render per animation frame). */
    const applyFlyFrame = useCallback(
        (frame) => {
            const node = flyNodeRef.current;
            const inner = flyInnerRef.current;
            if (!node) return;
            node.style.left = `${frame.left}px`;
            node.style.top = `${frame.top}px`;
            node.style.width = `${frame.width}px`;
            node.style.height = `${frame.height}px`;
            if (inner && pieceNativePx > 0) {
                inner.style.transform = `scale(${frame.width / pieceNativePx})`;
            }
        },
        [pieceNativePx],
    );

    useEffect(() => abortPending, [abortPending]);

    const runnerReady =
        !loading && !error && Boolean(basket) && grid > 0 && boardSide > 0 && Boolean(paths);

    useEffect(() => {
        if (!runnerReady) return undefined;

        let cancelled = false;

        const wait = (ms) =>
            new Promise((resolve) => {
                const entry = { resolve };
                entry.timerId = window.setTimeout(() => {
                    pendingRef.current = pendingRef.current.filter((e) => e !== entry);
                    resolve();
                }, ms);
                pendingRef.current.push(entry);
            });

        const placeAtHome = (lane, pieceIndex, withSound) => {
            const piece = homePiece(pieceIndex, grid, cellPx);
            const setter = lane === "basket" ? setBasketPieces : setMainPieces;
            setter((prev) =>
                prev.some((b) => b.pieceIndex === pieceIndex) ? prev : [...prev, piece],
            );
            runtimeRef.current.triggerShine(piece.id);
            if (withSound) void AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
        };

        const flyPiece = (lane, pieceIndex, durationMs, withSound) =>
            new Promise((resolve) => {
                const slot = traySlotRefs.current.get(`${lane}:${pieceIndex}`);
                const boardEl = lane === "basket" ? basketBoardRef.current : mainBoardRef.current;
                const toRect = homeScreenRect(pieceIndex, grid, boardEl);
                if (!slot || !toRect) {
                    placeAtHome(lane, pieceIndex, withSound);
                    resolve();
                    return;
                }
                slot.scrollIntoView?.({ block: "nearest", inline: "nearest" });
                const rect = slot.getBoundingClientRect();
                const fromRect = {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                };
                const entry = { resolve };
                setFlying({ lane, pieceIndex, frame: fromRect });
                entry.cancel = animatePieceAlongCurve({
                    fromRect,
                    toRect,
                    control: flyControlPoint(fromRect, toRect),
                    durationMs,
                    // Written straight to the node: a state update per frame cannot keep up
                    // with a full board and tray re-render.
                    onFrame: (frame) => applyFlyFrame(frame),
                    onDone: () => {
                        pendingRef.current = pendingRef.current.filter((e) => e !== entry);
                        setFlying(null);
                        placeAtHome(lane, pieceIndex, withSound);
                        resolve();
                    },
                });
                pendingRef.current.push(entry);
            });

        /** Fly the front of a lane's tray until `pick` runs out of pieces. */
        const flyRun = async (lane, pick, durationMs, gapMs, withSound) => {
            for (;;) {
                if (cancelled) return;
                const pieceIndex = pick();
                if (pieceIndex == null) return;
                await flyPiece(lane, pieceIndex, durationMs, withSound);
                if (cancelled) return;
                await wait(gapMs);
            }
        };

        const runOnce = async () => {
            const { basketPieceIds: basketIds, trayOrder: order } = runtimeRef.current;
            setPhase(PHASE.FEATURE);
            setMainPieces([]);
            setBasketPieces([]);
            setShines([]);
            setFlying(null);
            await wait(FEATURE_SHOT_MS);
            if (cancelled) return;

            setPhase(PHASE.SEED);
            await wait(INTRO_HOLD_MS);
            if (cancelled) return;

            const seedQueue = order
                .filter((idx) => !basketIds.includes(idx))
                .slice(0, SEED_PIECE_COUNT);
            await flyRun("main", () => seedQueue.shift() ?? null, SEED_FLY_MS, SEED_GAP_MS, true);
            if (cancelled) return;

            setPhase(PHASE.BASKET_TAP);
            await wait(BASKET_TAP_HOLD_MS);
            if (cancelled) return;

            // The chooser dialog auto-opens the basket, which moves the phase on.
            setPhase(PHASE.CHOOSER);
            await wait(CHOOSER_PICK_MS + 900);
            if (cancelled) return;

            setPhase(PHASE.BASKET);
            await wait(INTRO_HOLD_MS);
            if (cancelled) return;

            const basketQueue = basketIds.filter(
                (idx) => !mainPiecesRef.current.some((b) => b.pieceIndex === idx),
            );
            const basketFlyMs = paceFlyMs(
                basketQueue.length,
                BASKET_RUN_BUDGET_MS,
                BASKET_FLY_MIN_MS,
                BASKET_FLY_MAX_MS,
                BASKET_GAP_MS,
            );
            await flyRun(
                "basket",
                () => basketQueue.shift() ?? null,
                basketFlyMs,
                BASKET_GAP_MS,
                true,
            );
            if (cancelled) return;
            await wait(BASKET_DONE_HOLD_MS);
            if (cancelled) return;

            // Deploy: the finished basket board lands on the main board at home.
            const deployed = basketPiecesRef.current.map((b) => ({ ...b, id: newPieceId(b.pieceIndex) }));
            setPhase(PHASE.MIRROR);
            setBasketPieces([]);
            setMainPieces((prev) => {
                const have = new Set(prev.map((b) => b.pieceIndex));
                return [...prev, ...deployed.filter((b) => !have.has(b.pieceIndex))];
            });
            for (const piece of deployed) runtimeRef.current.triggerShine(piece.id);
            void AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
            await wait(MIRROR_HOLD_MS);
            if (cancelled) return;

            setPhase(PHASE.FINISH);
            const placed = new Set(mainPiecesRef.current.map((b) => b.pieceIndex));
            const finishQueue = order.filter((idx) => !placed.has(idx));
            const soloQueue = finishQueue.splice(0, FINISH_SOLO_COUNT);
            await flyRun(
                "main",
                () => soloQueue.shift() ?? null,
                FINISH_SOLO_FLY_MS,
                FINISH_SOLO_GAP_MS,
                true,
            );
            if (cancelled) return;

            while (finishQueue.length > 0) {
                const batch = finishQueue.splice(0, FINISH_SWEEP_BATCH);
                const pieces = batch.map((idx) => homePiece(idx, grid, cellPx));
                setMainPieces((prev) => [...prev, ...pieces]);
                for (const piece of pieces) runtimeRef.current.triggerShine(piece.id);
                void AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
                await wait(FINISH_SWEEP_STEP_MS);
                if (cancelled) return;
            }

            setPhase(PHASE.SOLVED);
            void AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
            runtimeRef.current.triggerConfetti(SOLVED_HOLD_MS);
            await wait(SOLVED_HOLD_MS);
        };

        // A new loop index reshuffles the tray, which restarts this effect for the next take.
        (async () => {
            await runOnce();
            if (cancelled) return;
            await wait(LOOP_GAP_MS);
            if (cancelled) return;
            setLoopIndex((n) => n + 1);
        })();

        return () => {
            cancelled = true;
            abortPending();
            setFlying(null);
        };
    }, [abortPending, applyFlyFrame, boardSide, cellPx, grid, loopIndex, runnerReady]);

    const registerTraySlot = useCallback((lane, pieceIndex, el) => {
        const key = `${lane}:${pieceIndex}`;
        if (el) traySlotRefs.current.set(key, el);
        else traySlotRefs.current.delete(key);
    }, []);

    const renderTrayPiece = useCallback(
        (lane, pieceIndex) => (
            <Box
                key={pieceIndex}
                ref={(el) => registerTraySlot(lane, pieceIndex, el)}
                sx={{
                    width: trayCirclePx,
                    height: trayCirclePx,
                    flexShrink: 0,
                    borderRadius: "50%",
                    overflow: "hidden",
                    position: "relative",
                    bgcolor: "rgba(255,255,255,0.35)",
                    opacity: flying?.pieceIndex === pieceIndex ? 0 : 1,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: 0,
                        height: 0,
                        overflow: "visible",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            transform: `translate(-50%, -50%) scale(${trayScale})`,
                            transformOrigin: "center center",
                            lineHeight: 0,
                        }}
                    >
                        <PieceVisual
                            pieceIndex={pieceIndex}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={paths}
                            showGridCoords={false}
                            anchorToCell={false}
                        />
                    </Box>
                </Box>
            </Box>
        ),
        [flying?.pieceIndex, grid, imageUrl, paths, registerTraySlot, trayCirclePx, trayScale],
    );

    if (loading) {
        return (
            <Box
                sx={{
                    width: FRAME.width,
                    height: FRAME.height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#c5d5ce",
                }}
            >
                <CircularProgress size={44} sx={{ color: "#3BA666" }} />
            </Box>
        );
    }

    if (error || !template || !imageUrl || grid <= 0 || !basket) {
        return (
            <Box
                sx={{
                    width: FRAME.width,
                    height: FRAME.height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                    bgcolor: "#c5d5ce",
                }}
            >
                <Typography color="error" variant="h6">
                    {error || "Color basket demo is not available for this puzzle"}
                </Typography>
            </Box>
        );
    }

    const staticToolbarIcon = (label, icon, extraSx = null) => (
        <IconButton
            aria-label={label}
            disableRipple
            sx={{ ...TOOLBAR_ICON_SX, flexShrink: 0, cursor: "default", ...extraSx }}
        >
            {icon}
        </IconButton>
    );

    const toolbarColumn = (children) => (
        <Box
            sx={{
                flexShrink: 0,
                width: TOOLBAR_COL_W,
                boxSizing: "border-box",
                mr: `${TOOLBAR_GAP}px`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.5,
                py: 2,
                px: 0.5,
                bgcolor: "rgba(255,255,255,0.72)",
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                overflow: "hidden",
            }}
        >
            {children}
        </Box>
    );

    const mainScene = (
        <>
            {toolbarColumn(
                <>
                    {staticToolbarIcon("Main play", <ArrowBackIcon />)}
                    <Box
                        sx={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flexShrink: 0,
                            "& .MuiTypography-root": {
                                fontSize: "1.5rem",
                                fontWeight: 800,
                                letterSpacing: "0.02em",
                            },
                        }}
                    >
                        <PlayElapsedTimer
                            active
                            running={!isSolved}
                            persistKey={`color-basket-demo-${loopIndex}`}
                            ariaLabel="Elapsed time"
                        />
                        {staticToolbarIcon("Pause timer", <PauseIcon />)}
                    </Box>
                    {staticToolbarIcon("Remove misplaced pieces", <CleaningServicesIcon />)}
                    {staticToolbarIcon("Hint", <LightbulbOutlinedIcon />)}
                    {staticToolbarIcon("Zoom in", <ZoomInIcon />)}
                    {staticToolbarIcon("Zoom out", <ZoomOutIcon />)}
                    {staticToolbarIcon("View original image", <OpenInNewIcon />)}
                    {staticToolbarIcon(
                        "Color baskets",
                        <FaShoppingBasket size={36} />,
                        phase === PHASE.BASKET_TAP || phase === PHASE.CHOOSER
                            ? {
                                  borderRadius: "50%",
                                  animation: `${basketTapPulse} 1s ease-in-out infinite`,
                              }
                            : null,
                    )}
                    {staticToolbarIcon("Settings", <SettingsIcon />)}
                </>,
            )}
            <Box
                sx={{
                    flex: "0 0 auto",
                    minHeight: 0,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        gap: `${TRAY_GAP}px`,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: boardSide,
                            height: boardSide,
                            flexShrink: 0,
                            position: "relative",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "& > *": { flexShrink: 0, margin: "auto" },
                        }}
                    >
                        {phase === PHASE.SOLVED ? (
                            <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                sx={{
                                    width: boardSide,
                                    height: boardSide,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    display: "block",
                                }}
                            />
                        ) : (
                            <CanvasBoard
                                boardRef={mainBoardRef}
                                boardPieces={mainPieces}
                                grid={grid}
                                imageUrl={imageUrl}
                                paths={paths}
                                svgPathFallback={svgPathFallback}
                                dragging={null}
                                activePieceShines={shines}
                                showDragOutline={false}
                                placedCount={mainPieces.length}
                                total={total}
                                isSolved={isSolved}
                                showPieceGridCoords={false}
                                showHeader={false}
                                readOnly
                                fitParent={false}
                                matPreset={boardMat}
                                matColor={boardMat.hex}
                                grooveStyle={boardGrooveStyle}
                                explicitDisplaySidePx={boardSide}
                            />
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: trayThicknessPx,
                            height: boardSide,
                            flexShrink: 0,
                            overflow: "hidden",
                        }}
                    >
                        {phase === PHASE.SOLVED ? null : (
                            <PieceTrayScrollColumns
                                containerRef={mainTrayRef}
                                columnCount={1}
                                maxHeightPx={boardSide}
                                cellSizePx={trayCirclePx}
                                gapPx={TRAY_CELL_GAP}
                            >
                                {mainTrayPieces.map((pieceIndex) =>
                                    renderTrayPiece("main", pieceIndex),
                                )}
                            </PieceTrayScrollColumns>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );

    const basketScene = (
        <>
            {toolbarColumn(
                <>
                    {staticToolbarIcon("Main play", <ArrowBackIcon />)}
                    <Box
                        aria-hidden
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: basketHex,
                            border: "2px solid rgba(255,255,255,0.85)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        sx={{ fontWeight: 800, fontSize: "1.35rem", color: "#1B4332" }}
                    >
                        {`${basketPieces.length}/${basketPieceIds.length}`}
                    </Typography>
                    <PlayElapsedTimer
                        active
                        running
                        persistKey={`color-basket-demo-${loopIndex}`}
                        ariaLabel="Elapsed time"
                    />
                    {staticToolbarIcon("Piece map", <MapOutlinedIcon />)}
                    {staticToolbarIcon(
                        "Deploy collected pieces onto the main board",
                        <RocketLaunchIcon />,
                        basketTrayPieces.length === 0
                            ? {
                                  borderRadius: "50%",
                                  animation: `${basketTapPulse} 1s ease-in-out infinite`,
                              }
                            : { opacity: 0.4 },
                    )}
                </>,
            )}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: `${TRAY_GAP}px`,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    <CanvasBoard
                        boardRef={basketBoardRef}
                        boardPieces={basketPieces}
                        grid={grid}
                        imageUrl={imageUrl}
                        paths={paths}
                        svgPathFallback={svgPathFallback}
                        dragging={null}
                        activePieceShines={shines}
                        showDragOutline={false}
                        placedCount={basketPieces.length}
                        total={basketPieceIds.length}
                        isSolved={basketTrayPieces.length === 0}
                        showPieceGridCoords={false}
                        showHeader={false}
                        readOnly
                        lockHomePieces
                        showDieLines={false}
                        regionGuide={regionGuide}
                        cropToRegionGuide
                        cropPaddingCells={PIECE_VISUAL_MARGIN_RATIO}
                        cropMaxHeight={0}
                        matPreset={boardMat}
                        matColor={boardMat.hex}
                        grooveStyle={boardGrooveStyle}
                        fitParent
                    />
                </Box>
                <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    <PieceTrayScrollRows
                        containerRef={basketTrayRef}
                        rowCount={1}
                        maxWidthPx={FRAME.width - TOOLBAR_COL_W - TOOLBAR_GAP - PAD * 2}
                        cellSizePx={trayCirclePx}
                        gapPx={TRAY_CELL_GAP}
                    >
                        {basketTrayPieces.map((pieceIndex) =>
                            renderTrayPiece("basket", pieceIndex),
                        )}
                    </PieceTrayScrollRows>
                </Box>
            </Box>
        </>
    );

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: FRAME.width,
                height: FRAME.height,
                maxWidth: FRAME.width,
                maxHeight: FRAME.height,
                overflow: "hidden",
                boxSizing: "border-box",
                p: `${PAD}px`,
                display: "flex",
                flexDirection: "column",
                bgcolor: "#c5d5ce",
            }}
        >
            <SlidingPlayMistBackground />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    overflow: "hidden",
                }}
            >
                {phase === PHASE.FEATURE ? (
                    <ColorBasketFeatureShot
                        baskets={baskets}
                        imageUrl={imageUrl}
                        grid={grid}
                        puzzleTitle={puzzleTitle}
                        highlightBasketIndex={basketIndex}
                    />
                ) : onBasketScene ? (
                    basketScene
                ) : (
                    mainScene
                )}
            </Box>

            {flying && paths ? (
                <Box
                    ref={flyNodeRef}
                    sx={{
                        position: "fixed",
                        left: flying.frame.left,
                        top: flying.frame.top,
                        width: flying.frame.width,
                        height: flying.frame.height,
                        pointerEvents: "none",
                        zIndex: 9999,
                    }}
                >
                    <Box
                        ref={flyInnerRef}
                        sx={{
                            transform: `scale(${
                                pieceNativePx > 0 ? flying.frame.width / pieceNativePx : 1
                            })`,
                            transformOrigin: "top left",
                            lineHeight: 0,
                        }}
                    >
                        <PieceVisual
                            pieceIndex={flying.pieceIndex}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={paths}
                            showGridCoords={false}
                            anchorToCell={false}
                        />
                    </Box>
                </Box>
            ) : null}

            <JigsawColorBasketOverlay
                open={phase === PHASE.CHOOSER}
                onClose={() => {}}
                storyId={RECORDING_CONTENT_STORY_ID}
                jigsawId={template.id}
                grid={grid}
                imageUrl={imageUrl}
                puzzleTitle={puzzleTitle}
                onBasketChosen={() => setPhase(PHASE.BASKET)}
                autoPickBasketIndex={basketIndex}
                autoPickDelayMs={CHOOSER_PICK_MS}
                dialogSx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: FRAME.width,
                    height: FRAME.height,
                }}
                paperSx={{ maxHeight: Math.round(FRAME.height * 0.9) }}
            />
        </Box>
    );
}
